import { Box, TextField } from '@mui/material';
import { Typography } from '@peritasai/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailsDialog } from '../shared/DetailsDialog';
import { NoteField } from '../shared/NoteField';

export interface InstrumentDetails {
  note: string | null;
  group: string | null;
  set: string | null;
}

export interface InstrumentDetailsDialogProps {
  open: boolean;
  initialNote: string | null;
  initialGroup: string | null;
  initialSet: string | null;
  onSave: (details: InstrumentDetails) => void;
  onClose: () => void;
}

const MAX_NOTE_LENGTH = 1000;
const MAX_GROUP_LENGTH = 255;
const MAX_SET_LENGTH = 255;

export const InstrumentDetailsDialog = ({
  open,
  initialNote,
  initialGroup,
  initialSet,
  onSave,
  onClose,
}: InstrumentDetailsDialogProps) => {
  const { t } = useTranslation(['periverse']);
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [groupDraft, setGroupDraft] = useState<string | null>(null);
  const [setDraft, setSetDraft] = useState<string | null>(null);
  const note = noteDraft ?? initialNote ?? '';
  const group = groupDraft ?? initialGroup ?? '';
  const set = setDraft ?? initialSet ?? '';

  const handleClose = () => {
    setNoteDraft(null);
    setGroupDraft(null);
    setSetDraft(null);
    onClose();
  };

  const handleSave = () => {
    onSave({ note: note.trim() || null, group: group.trim() || null, set: set.trim() || null });
    handleClose();
  };

  return (
    <DetailsDialog
      open={open}
      isEditing={!!(initialNote || initialGroup || initialSet)}
      title={t('pages.entityManagement.instrumentDetailsDialog.title')}
      saveLabel={t('pages.entityManagement.instrumentDetailsDialog.save')}
      saveTestId="instrument-details-save"
      saveDisabled={note.length > MAX_NOTE_LENGTH}
      onSave={handleSave}
      onClose={handleClose}
    >
      <Box>
        <Typography
          variant="body2"
          display="block"
          sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '17px', mb: 0.5 }}
        >
          {t('pages.entityManagement.instrumentDetailsDialog.groupLabel')}
        </Typography>
        <TextField
          fullWidth
          value={group}
          onChange={(e) => setGroupDraft(e.target.value)}
          slotProps={{
            htmlInput: {
              'aria-label': t('pages.entityManagement.instrumentDetailsDialog.groupLabel'),
              'data-testid': 'instrument-details-group-input',
              maxLength: MAX_GROUP_LENGTH,
            },
          }}
        />
      </Box>
      <Box>
        <Typography
          variant="body2"
          display="block"
          sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '17px', mb: 0.5 }}
        >
          {t('pages.entityManagement.instrumentDetailsDialog.setLabel')}
        </Typography>
        <TextField
          fullWidth
          value={set}
          onChange={(e) => setSetDraft(e.target.value)}
          slotProps={{
            htmlInput: {
              'aria-label': t('pages.entityManagement.instrumentDetailsDialog.setLabel'),
              'data-testid': 'instrument-details-set-input',
              maxLength: MAX_SET_LENGTH,
            },
          }}
        />
      </Box>
      <NoteField
        label={t('pages.entityManagement.instrumentDetailsDialog.noteLabel')}
        value={note}
        onChange={setNoteDraft}
        testId="instrument-details-note-input"
        helperText={t('pages.entityManagement.instrumentDetailsDialog.charCount', {
          current: String(note.length),
          max: String(MAX_NOTE_LENGTH),
        })}
      />
    </DetailsDialog>
  );
};
